import UPInput from 'components/input';
import { UPTanStackTable } from 'components/tanstack-table';
import { PayrollAddNewItemButton } from 'modules/companies/payroll/styles';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildParitairComiteDetailsRoute, NEW_PARITAIR_COMITE_ID } from 'modules/management/joint-commissions/routes';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import { getParitairComitesAction } from 'store/actions/paritair-comite-actions';
import { paritairComitesSelector } from 'store/selectors/paritair-comite-selectors';
import { DATAGRID__NO_ROWS, GENERAL__SEARCH, PARITAIR_COMITE__ADD_PARITAIR_COMITE } from 'translations/constants';
import { IParitairComite } from 'types/paritair-comite';
import { escapeRegExp } from 'utils/helpers';
import { buildParitairComiteColumns } from './columns';
import { ParitairComiteContainer, SearchContainer, TopSectionContainer } from './styles';

const ParitairComite = (): JSX.Element => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const paritairComites = useAppSelector(paritairComitesSelector);

  const [searchText, setSearchText] = useState('');
  const [rows, setRows] = useState<IParitairComite[]>([]);

  const requestSearch = (searchValue: string) => {
    setSearchText(searchValue);
    const searchRegex = new RegExp(escapeRegExp(searchValue), 'i');
    const filteredRows = paritairComites.filter(row => {
      return Object.keys(row).some((field: string) => {
        return searchRegex.test(row[field].toString());
      });
    });
    setRows(filteredRows);
  };

  useEffect(() => {
    dispatch(getParitairComitesAction());
  }, [dispatch]);

  useEffect(() => {
    if (paritairComites) {
      setRows(paritairComites);
    }
  }, [paritairComites]);

  const onEdit = useCallback((row: IParitairComite) => navigate(buildParitairComiteDetailsRoute(row.id)), [navigate]);
  const columns = useMemo(() => buildParitairComiteColumns(t, onEdit), [t, onEdit]);

  return (
    <ParitairComiteContainer>
      <TopSectionContainer sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <SearchContainer>
          <UPInput
            value={searchText}
            onChange={(event: ChangeEvent<HTMLInputElement>) => requestSearch(event.target.value)}
            label=""
            placeholder={`${t(GENERAL__SEARCH)}...`}
            icon="search"
          />
        </SearchContainer>
        <PayrollAddNewItemButton
          text={`+ ${t(PARITAIR_COMITE__ADD_PARITAIR_COMITE)}`}
          onClick={() => navigate(buildParitairComiteDetailsRoute(NEW_PARITAIR_COMITE_ID))}
        />
      </TopSectionContainer>
      <UPTanStackTable
        data={rows}
        columns={columns}
        getRowId={row => row.id}
        emptyMessage={t(DATAGRID__NO_ROWS)}
        sx={{
          height: 'auto',
          '& .MuiTableContainer-root': { maxHeight: 'none', overflowY: 'visible' },
          '& thead th:last-of-type:hover': { backgroundColor: 'common.white' },
        }}
      />
    </ParitairComiteContainer>
  );
};

export default ParitairComite;
